import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Column, Row, Spinner, TextButton, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import GenericError from '~/components/GenericError/GenericError';
import { useLocaleKeys } from '~/i18n';
import { usePulseOpportunitiesWidget } from './hooks/usePulseOpportunitiesWidget';
import { OpportunityRow } from './OpportunityRow/OpportunityRow';
import styles from './PulseOpportunitiesWidget.module.scss';
import { formatSyncDate } from './utils/formatOpportunityValues';
import { opportunityIdentity } from './utils/opportunityIdentity';
export const GetPulseOpportunitiesWidgetTID = (parentTestId)=>getTestIds(parentTestId);
export const PulseOpportunitiesWidget = (param)=>{
    let { testId } = param;
    const { PulseOpportunitiesWidgetTestIds } = useMemo(()=>GetPulseOpportunitiesWidgetTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys('ai.pulse');
    const { rootRef, state, visible, hiddenCount, isExpanded, feedId, lastSyncedAt, locale, clientNamesById, toggleExpanded, retry } = usePulseOpportunitiesWidget();
    return /*#__PURE__*/ _jsxs(Column, {
        ref: rootRef,
        testId: PulseOpportunitiesWidgetTestIds.Root(),
        gap: 16,
        className: styles.root,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                weight: "normal",
                skin: "secondary",
                testId: PulseOpportunitiesWidgetTestIds.Title(),
                children: texts.title()
            }),
            state === 'loading' && /*#__PURE__*/ _jsxs(Row, {
                gap: 8,
                alignItems: "center",
                testId: PulseOpportunitiesWidgetTestIds.Loading(),
                children: [
                    /*#__PURE__*/ _jsx(Spinner, {
                        size: 24
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        children: texts.states.loading()
                    })
                ]
            }),
            state === 'error' && /*#__PURE__*/ _jsxs(Column, {
                gap: 8,
                alignItems: "center",
                testId: PulseOpportunitiesWidgetTestIds.Error(),
                children: [
                    /*#__PURE__*/ _jsx(GenericError, {
                        message: texts.states.error()
                    }),
                    /*#__PURE__*/ _jsx(TextButton, {
                        bi: {
                            name: 'pulse_opportunities_retry'
                        },
                        testId: PulseOpportunitiesWidgetTestIds.Retry(),
                        onClick: retry,
                        children: texts.states.retry()
                    })
                ]
            }),
            state === 'empty' && /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph2",
                skin: "secondary",
                testId: PulseOpportunitiesWidgetTestIds.Empty(),
                children: texts.states.empty()
            }),
            state === 'populated' && /*#__PURE__*/ _jsx(Column, {
                gap: 12,
                children: visible.map((opportunity, index)=>{
                    var _clientNamesById_get;
                    return /*#__PURE__*/ _jsx(OpportunityRow, {
                        testId: PulseOpportunitiesWidgetTestIds.Row(index),
                        opportunity: opportunity,
                        clientLabel: (_clientNamesById_get = clientNamesById.get(opportunity.clientId)) !== null && _clientNamesById_get !== void 0 ? _clientNamesById_get : texts.unknownClient(),
                        locale: locale,
                        position: index,
                        feedId: feedId
                    }, opportunityIdentity(opportunity));
                })
            }),
            hiddenCount > 0 && /*#__PURE__*/ _jsxs(Row, {
                gap: 8,
                alignItems: "center",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph3",
                        skin: "secondary",
                        testId: PulseOpportunitiesWidgetTestIds.Disclosure(),
                        children: texts.disclosure.hiddenCount({
                            count: hiddenCount
                        })
                    }),
                    /*#__PURE__*/ _jsx(TextButton, {
                        bi: {
                            name: 'pulse_opportunities_see_all'
                        },
                        testId: PulseOpportunitiesWidgetTestIds.SeeAll(),
                        onClick: toggleExpanded,
                        children: texts.disclosure.seeAll()
                    })
                ]
            }),
            isExpanded && /*#__PURE__*/ _jsx(TextButton, {
                bi: {
                    name: 'pulse_opportunities_show_less'
                },
                testId: PulseOpportunitiesWidgetTestIds.ShowLess(),
                onClick: toggleExpanded,
                children: texts.disclosure.showLess()
            }),
            (state === 'populated' || state === 'empty') && /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph3",
                skin: "secondary",
                testId: PulseOpportunitiesWidgetTestIds.Freshness(),
                children: lastSyncedAt == null ? texts.freshness.unknown() : texts.freshness.known({
                    date: formatSyncDate(lastSyncedAt, locale)
                })
            })
        ]
    });
};
